<template>
    <div class="admin-content">
        <div class="row">
            <div class="col-sm-12">
                <div class="alert alert-warning" role="alert" style="padding: 15px" v-if="activeUntil != null">
                    <strong>Pending changes!</strong>
                    Changes pending until at least {{ activeUntil.toLocaleTimeString() }}
                </div>
                <h1 class="admin-content-heading" id="FeatureFlagsHeading">Feature Flags</h1>
                <table class="table table-sm table-hover" role="main" aria-labelledby="FeatureFlagsHeading">
                    <thead>
                        <tr>
                            <th>Label</th>
                            <th>Mode</th>
                            <th>Global Enabled</th>
                            <th>Tenant Enabled</th>
                            <th>User Enabled</th>
                            <th><span class="sr-only">Actions</span></th>
                        </tr>
                    </thead>
                    <tbody>
                        <template v-for="flag in flags">
                            <tr>
                                <td>
                                    {{ flag.label }}
                                    <HelpIcon :text="flag.impact" />
                                </td>
                                <td>{{ flag.modeLabel() }}</td>
                                <td v-if="hasScope(flag, 'global')" @click.prevent="flipEnabled(flag, 'global')">
                                    <a href="#" :id="flag.id + '_GlobalToggle'" v-if="canEditFlag(flag)" tabindex="0"><StatusDisplay :status="flag.global" :global="true" :override="flag.globalOverride" /></a>
                                    <span v-else><StatusDisplay :status="flag.global" :override="flag.globalOverride" /></span>
                                </td>
                                <td v-else>---</td>
                                <td v-if="hasScope(flag, 'tenant')" @click.prevent="flipEnabled(flag, 'currentTenant')">
                                    <!--
                                [{{ flag }}]
                                [{{ getCurrentTenantStatus(flag) }}]
                                -->
                                    <a href="#" :id="flag.id + '_CurrentTenantToggle'" v-if="canEditFlag(flag)" tabindex="0"><StatusDisplay :status="getCurrentTenantStatus(flag)" :override="getCurrentTenantOverride(flag)" /></a>
                                    <span v-else><StatusDisplay :status="getCurrentTenantStatus(flag)" :override="getCurrentTenantOverride(flag)" /></span>
                                </td>
                                <td v-else>---</td>
                                <td v-if="hasScope(flag, 'user')" @click.prevent="flipEnabled(flag, 'currentUser')">
                                    <!--
                                [{{ flag }}]
                                [{{ getCurrentUserStatus(flag) }}]
                                -->
                                    <a href="#" :id="flag.id + '_CurrentUserToggle'" v-if="canEditFlag(flag)" tabindex="0"><StatusDisplay :status="getCurrentUserStatus(flag)" :override="getCurrentUserOverride(flag)" /></a>
                                    <span v-else><StatusDisplay :status="getCurrentUserStatus(flag)" :override="getCurrentTenantOverride(flag)" /></span>
                                </td>
                                <td v-else>---</td>
                                <td v-if="tenants.length + users.length > 0 && (hasScope(flag, 'tenant') || hasScope(flag, 'user'))" @click.prevent="toggleFlagOpen(flag)"><a tabindex="0" href="#">Toggle Details</a></td>
                            </tr>
                            <tr>
                                <td colspan="6" v-show="isFlagOpen(flag)" style="display: none; border-left: #999999 solid 5px">
                                    <div class="row">
                                        <div class="col-md-12" v-if="tenants.length > 0 && hasScope(flag, 'tenant')">
                                            <h3 class="text-center">Tenants</h3>
                                            <table class="col-12">
                                                <tr v-for="tenantGroup in splitIntoGroups(tenants, 2)">
                                                    <template v-for="tenant in tenantGroup">
                                                        <td @click.prevent="flipEnabled(flag, 'otherTenant', tenant.code)">
                                                            <a v-if="canEditFlag(flag)" tabindex="0">
                                                                <StatusDisplay :status="flag.getTenantStatus(tenant.code)" :override="flag.getTenantOverride(tenant.code)" />
                                                            </a>
                                                            <StatusDisplay v-else :status="flag.getTenantStatus(tenant.code)" :override="flag.getTenantOverride(tenant.code)" />
                                                        </td>
                                                        <td>{{ tenant.display }}</td>
                                                    </template>
                                                </tr>
                                            </table>
                                        </div>
                                        <div class="col-md-12" v-if="users.length > 0 && hasScope(flag, 'user')">
                                            <h3 class="text-center">Users</h3>
                                            <table class="col-12">
                                                <tr v-for="userGroup in splitIntoGroups(users, 2)">
                                                    <template v-for="user in userGroup">
                                                        <td @click.prevent="flipEnabled(flag, 'otherUser', user.id)">
                                                            <a v-if="canEditFlag(flag)" tabindex="0">
                                                                <StatusDisplay :status="flag.getUserStatus(user.id)" :override="flag.getUserOverride(user.id)" />
                                                            </a>
                                                            <StatusDisplay v-else :status="flag.getUserStatus(user.id)" :override="flag.getUserOverride(user.id)" />
                                                        </td>
                                                        <td>{{ user.label }}</td>
                                                    </template>
                                                </tr>
                                            </table>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        </template>
                    </tbody>
                </table>
                <div v-show="hasMassUpgradeableFlags && canEdit">
                    <h3>Mass Upgradeable Flags:</h3>
                    <ul>
                        <li v-for="flag in massUpgradeableFlags">{{ flag.label }}</li>
                    </ul>
                    <div class="form-group">
                        <label for="massUpgradeTarget" class="control-label">Upgrade Target</label>
                        <select id="massUpgradeTarget" class="form-control" v-model="massUpgradeTarget">
                            <option :value="null">-- Select a target --</option>
                            <option value="Global">Global</option>
                            <option value="CurrentTenant">Current Tenant</option>
                            <option value="OtherTenant">Other Tenant</option>
                            <option value="CurrentUser">Current User</option>
                            <option value="OtherUser">Other User</option>
                        </select>
                    </div>
                    <div v-show="massUpgradeTarget == 'OtherTenant'" class="form-group">
                        <label for="massUpgradeTenant" class="control-label">Select a Tenant</label>
                        <select id="massUpgradeTenant" class="form-control" v-model="massUpgradeTenant">
                            <option :value="null">-- Select a tenant --</option>
                            <option v-for="tenant in tenants" :value="tenant.code">{{ tenant.display }}</option>
                        </select>
                    </div>
                    <div v-show="massUpgradeTarget == 'OtherUser'" class="form-group">
                        <label for="massUpgradeId" class="control-label">Select a User</label>
                        <select id="massUpgradeId" data-bind="value: massUpgradeUser" class="form-control">
                            <option :value="null">-- Select a user --</option>
                            <option v-for="user in users" :value="user.id">{{ user.label }}</option>
                        </select>
                    </div>
                    <div class="form-group">
                        <button class="btn btn-danger" @click.prevent="doMassUpgrade">Confirm</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script lang="ts">
import { defineComponent, ref, computed, watch } from "vue";
import { throwIfNonSuccessStatus } from "@/lib/fetchHelpers.3";
import { getXsftHeader } from "@/lib/getXsrfToken.3";
import getData from "../../lib/getData";
//import NonIndexStyles from '@/lib/nonIndexStyles.3.vue';
import StatusDisplay from "./Status.3.vue";
import { useMainStore } from "@/mainStore";
import { FeatureFlagsViewModel } from "@cs/FeatureFlagsViewModel";
import { SetResult } from "@cs/SetResult";
import { MassUpgradeResult } from "@cs/MassUpgradeResult";
import HelpIcon from "@/lib/HelpIcon.3.vue";

const targetMap = {
    Global: 0,
    CurrentTenant: 1,
    OtherTenant: 2,
    CurrentUser: 3,
    OtherUser: 4,
};
const modeMap = {
    Disabled: 0,
    Manual: 1,
    MassUpgrade: 2,
    Automatic: 3,
};
// https://stackoverflow.com/a/23013726
function objectFlip<K extends number | string, V extends number | string>(obj: Record<K, V>): Record<V, K> {
    const ret = {} as Record<V, K>;
    (Object.keys(obj) as K[]).forEach((key: K) => {
        ret[obj[key]] = key;
    });
    return ret;
}
function mathBound(value: number, min: number, max: number) {
    return Math.max(Math.min(value, max), min);
}
const modeReverseMap = objectFlip(modeMap);
//TODO duplicate with Status.vue
interface FeatureFlagState {
    current?: boolean | null;
    pending?: boolean | null;
    pendingSet?: boolean | null;
    saveStatus?: string | null;
    saveErrors?: string[];
}
class FeatureFlag {
    declare id: string;
    declare label: string;
    declare impact: string;
    declare mode: number;
    declare global: FeatureFlagState;
    declare globalOverride: boolean | undefined;
    declare tenants: Record<string, FeatureFlagState>;
    declare users: Record<string, FeatureFlagState>;
    declare usersOverride: Record<string, boolean>;
    declare tenantsOverride: Record<string, boolean>;
    declare scope: number;
    constructor(id: string, label: string, impact: string, mode: number, scope: number, global: FeatureFlagState, globalOverride: boolean | undefined, tenants: Record<string, FeatureFlagState>, tenantsOverride: Record<string, boolean>, users: Record<string, FeatureFlagState>, usersOverride: Record<string, boolean>) {
        this.id = id;
        this.label = label;
        this.impact = impact;
        this.mode = mode;
        this.global = global || { current: false, pending: true };
        this.globalOverride = globalOverride;
        this.tenants = {};
        this.users = {};
        this.tenantsOverride = {};
        this.usersOverride = {};
        this.scope = scope;
        Object.keys(users).forEach(k => {
            this.users[k] = users[k];
        });
        Object.keys(usersOverride).forEach(k => {
            this.usersOverride[k] = usersOverride[k];
        });
        Object.keys(tenants).forEach(k => {
            this.tenants[k] = tenants[k];
        });
        Object.keys(tenantsOverride).forEach(k => {
            this.tenantsOverride[k] = tenantsOverride[k];
        });
    }
    modeLabel() {
        return modeReverseMap[this.mode];
    }
    isDisabled() {
        return this.mode == modeMap["Disabled"]; // TODO?
    }
    getTenantStatus(tenantCode: string) {
        const tenantData = this.tenants[tenantCode];
        return tenantData != null ? tenantData : {};
    }
    getTenantOverride(tenantCode: string) {
        return this.tenantsOverride[tenantCode];
    }
    getUserStatus(userId: string) {
        const userData = this.users[userId];
        return userData != null ? userData : {};
    }
    getUserOverride(userId: string) {
        return this.usersOverride[userId];
    }
}
export default defineComponent({
    components: {
        StatusDisplay,
        HelpIcon,
    },
    setup() {
        const mainStore = useMainStore();
        const canEdit = ref(true);
        const massUpgradeTarget = ref(null as null | string);
        const massUpgradeTenant = ref(null as null | string);
        const massUpgradeUser = ref(null as null | string);
        const massUpgradeStatus = ref(null as null | string);
        const massUpgradeErrors = ref(null as null | string[]);
        const tenants = ref([] as { code: string; display: string }[]);
        const users = ref([] as { id: string; label: string }[]);
        const flags = ref([] as FeatureFlag[]);
        const activeUntil = ref(null as Date | null);
        const pendingCheck = ref(null as null | boolean);
        const openFlags = ref({} as Record<string, boolean>);
        const userId = ref(null! as string);
        //tenants: [],
        //users: [],
        //flags: [
        //    new FeatureFlag("FLAG", "Default Flag", "Default", {current: true, pending: null}),
        //    new FeatureFlag("FLAG-D", "Disabled Flag", "Disabled", {current: true, pending: false}),
        //    new FeatureFlag("FLAG-MU", "Mass Upgrade Flag", "MassUpgrade", {current: true, pending: null}),
        //],

        const hasMassUpgradeableFlags = computed(() => {
            return massUpgradeableFlags.value.length > 0;
        });
        const massUpgradeableFlags = computed(() => {
            return flags.value.filter(i => i.mode == modeMap["MassUpgrade"] && i.global.current !== true);
        });

        const isFlagOpen = (flag: FeatureFlag) => {
            return openFlags.value[flag.id];
        };
        const toggleFlagOpen = (flag: FeatureFlag) => {
            openFlags.value[flag.id] = !isFlagOpen(flag);
        };
        const canEditFlag = (flag: FeatureFlag) => {
            return canEdit.value && !flag.isDisabled();
        };
        const getCurrentTenantStatus = (flag: FeatureFlag) => {
            return flag.getTenantStatus(mainStore.tenantCode);
        };
        const getCurrentUserStatus = (flag: FeatureFlag) => {
            return flag.getUserStatus(userId.value);
        };
        const getCurrentTenantOverride = (flag: FeatureFlag) => {
            return flag.getTenantOverride(mainStore.tenantCode);
        };
        const getCurrentUserOverride = (flag: FeatureFlag) => {
            return flag.getUserOverride(userId.value);
        };
        const splitIntoGroups = <T,>(list: T[], size: number): T[][] => {
            const result = [];
            for (let index = 0; index < list.length; index += size) {
                result.push(list.slice(index, index + size));
            }
            return result;
        };
        const hasScope = (flag: FeatureFlag, scope: string) => {
            const scopeBit = scope == "global" ? 1 : scope == "tenant" ? 2 : scope == "user" ? 4 : 0;
            return (flag.scope & scopeBit) > 0;
        };
        const flipEnabled = async (flag: FeatureFlag, type: string, otherId?: string) => {
            if (!canEditFlag(flag)) {
                return;
            }
            let get: () => FeatureFlagState, set: (i: FeatureFlagState) => void, target: number;
            let tenantCodeI = mainStore.tenantCode;
            let userIdI = userId.value;
            if (type == "global") {
                get = () => flag.global;
                set = newData => {
                    flag.global = newData;
                };
                target = targetMap.Global;
            } else if (type == "currentTenant") {
                get = () => flag.tenants[tenantCodeI];
                set = newData => {
                    flag.tenants[tenantCodeI] = newData;
                };
                target = targetMap.CurrentTenant;
            } else if (type == "currentUser") {
                get = () => flag.users[userIdI];
                set = newData => {
                    flag.users[userIdI] = newData;
                };
                target = targetMap.CurrentUser;
            } else if (type == "otherTenant") {
                get = () => flag.tenants[otherId!];
                set = newData => {
                    flag.tenants[otherId!] = newData;
                };
                target = targetMap.OtherTenant;
                tenantCodeI = otherId!;
            } else if (type == "otherUser") {
                get = () => flag.users[otherId!];
                set = newData => {
                    flag.users[otherId!] = newData;
                };
                target = targetMap.OtherUser;
                userIdI = otherId!;
            } else {
                debugger;
                throw new Error("Unknown type: " + type);
            }
            var data = get();
            data = data || { current: null, pending: null };
            var pendingValue = data.pendingSet ? data.pending : data.current;
            var newValue = pendingValue == null ? true : pendingValue === true ? false : type === "global" ? true : null;
            data.pending = newValue;
            data.pendingSet = true;
            data.saveStatus = "in-progress";
            set(data);

            const saveUrl = await mainStore.getUrl("adminFeatureFlagsSetFlag");
            const formData = new FormData();
            formData.append("id", flag.id);
            formData.append("target", target?.toString() ?? "");
            if (tenantCodeI != null) {
                formData.append("tenantCode", tenantCodeI);
            }
            if (userIdI != null) {
                formData.append("user", userIdI);
            }
            formData.append("enabled", newValue?.toString() ?? "");
            try {
                const saveResponse = await fetch(saveUrl, {
                    method: "POST",
                    credentials: "same-origin",
                    headers: await getXsftHeader(mainStore),
                    body: formData,
                });
                throwIfNonSuccessStatus(saveResponse);
                const saveJson = (await saveResponse.json()) as SetResult;
                if (saveJson.Success) {
                    data.saveStatus = "ok";
                    const effectiveOn = new Date(saveJson.EffectiveOn);
                    if (activeUntil.value == null || activeUntil.value > effectiveOn) {
                        activeUntil.value = effectiveOn;
                    }
                } else {
                    data.saveStatus = "fail";
                    data.saveErrors = saveJson.Error || ["Unknown error occurred"];
                }
            } catch (e) {
                data.saveStatus = "fail";
                data.saveErrors = ["Unknown error occurred"];
                throw e;
            }
            //$forceUpdate();
        };
        const doMassUpgrade = async () => {
            massUpgradeStatus.value = "in-progress";
            const saveUrl = await mainStore.getUrl("adminFeatureFlagsMassUpgradeFlags");
            const formData = new FormData();
            formData.append("target", targetMap[massUpgradeTarget.value as keyof typeof targetMap]?.toString());
            if (massUpgradeTarget.value == "OtherTenant") {
                formData.append("tenantCode", massUpgradeTenant.value ?? "");
            }
            if (massUpgradeTarget.value == "OtherUser") {
                formData.append("tenantCode", massUpgradeUser.value ?? "");
            }
            try {
                const saveResponse = await fetch(saveUrl, {
                    method: "POST",
                    credentials: "same-origin",
                    headers: await getXsftHeader(mainStore),
                    body: formData,
                });
                throwIfNonSuccessStatus(saveResponse);
                const saveJson = (await saveResponse.json()) as MassUpgradeResult;
                if (saveJson.Success) {
                    massUpgradeStatus.value = "ok";
                    applyData(null); // TODO read data from response?
                } else {
                    massUpgradeStatus.value = "fail";
                    massUpgradeErrors.value = saveJson.ItemResults?.flatMap(i => i.Error ?? []) || ["Unknown error occurred"];
                }
            } catch {
                massUpgradeStatus.value = "fail";
                massUpgradeErrors.value = ["Unknown error occurred"];
            }
        };
        const applyData = async (featureFlagData: FeatureFlagsViewModel | null) => {
            if (featureFlagData == null) {
                const dataUrl = await mainStore.getUrl("adminFeatureFlagsData");
                const dataResponse = await fetch(dataUrl);
                throwIfNonSuccessStatus(dataResponse);
                featureFlagData = (await dataResponse.json()) as FeatureFlagsViewModel;
            }
            userId.value = featureFlagData.UserId!;
            const now = Date.now();
            const dataActiveUntil = featureFlagData.ActiveUntil != null ? new Date(featureFlagData.ActiveUntil) : null;
            if (activeUntil.value != null && activeUntil.value.getTime() <= now) {
                activeUntil.value = null;
            }
            if (dataActiveUntil != null && (activeUntil.value == null || activeUntil.value <= dataActiveUntil)) {
                activeUntil.value = dataActiveUntil;
            }
            flags.value = featureFlagData.FeatureFlags!.map(item => {
                const current = { current: item.GlobalEnabledCurrently, pending: item.GlobalEnabledPending };
                const tenant = {
                    [mainStore.tenantCode]: {
                        current: item.CurrentTenantEnabledCurrently,
                        pending: (item.CurrentTenantEnabledPending || {}).Value,
                        pendingSet: item.CurrentTenantEnabledPending != null,
                    },
                } as Record<string, FeatureFlagState>;
                Object.entries(item.TenantEnabledCurrently || {}).forEach(([k, v]) => {
                    tenant[k] = tenant[k] || {};
                    tenant[k].current = v;
                    tenant[k].pending = null;
                    tenant[k].pendingSet = null;
                    tenant[k].saveStatus = null;
                });
                Object.entries(item.TenantEnabledPending || {}).forEach(([k, v]) => {
                    tenant[k] = tenant[k] || {};
                    tenant[k].current = tenant[k].current || null;
                    tenant[k].pending = v == null ? null : v;
                    tenant[k].pendingSet = v != null;
                });
                const user = {
                    [userId.value]: {
                        current: item.CurrentUserEnabledCurrently,
                        pending: (item.CurrentUserEnabledPending || {}).Value,
                        pendingSet: item.CurrentUserEnabledPending != null,
                    } as FeatureFlagState,
                } as Record<string, FeatureFlagState>;
                Object.entries(item.UserEnabledCurrently || {}).forEach(([k, v]) => {
                    user[k] = user[k] || {};
                    user[k].current = v;
                    user[k].pending = null;
                    user[k].pendingSet = null;
                    user[k].saveStatus = null;
                });
                Object.entries(item.UserEnabledPending || {}).forEach(([k, v]) => {
                    user[k] = user[k] || {};
                    user[k].current = user[k].current || null;
                    user[k].pending = v == null ? null : v;
                    user[k].pendingSet = v != null;
                });
                var override = featureFlagData!.FeatureFlagOverrides?.find(i => i.Id == item.Id);
                //if (override != null) {
                //    debugger;
                //}
                return new FeatureFlag(item.Id!, item.Label!, item.Impact!, item.Mode, item.Scope, current, override?.GlobalEnabled, tenant, override?.TenantEnabled ?? {}, user, override?.UserEnabled ?? {});
            });
            const tenantDisplayLabels = mainStore.getTenantDisplayLabel((featureFlagData.Tenants || []).map(i => i));
            tenants.value = await Promise.all((featureFlagData.Tenants || []).map(async i => ({ code: i, display: await tenantDisplayLabels[i] })));
            users.value = Object.keys(featureFlagData.Users || {}).map(id => ({ id: id, label: featureFlagData!.Users![id] }));
        };
        watch(
            () => activeUntil.value,
            () => {
                if (activeUntil.value != null && !pendingCheck.value) {
                    pendingCheck.value = true;
                    setTimeout(
                        () => {
                            pendingCheck.value = false;
                            applyData(null);
                        },
                        mathBound(activeUntil.value.getTime() - Date.now(), 1 * 1000, 120 * 1000),
                    );
                }
            },
        );
        (async () => {
            const featureFlagData = await getData(mainStore, "AdminFeatureFlags", () => mainStore.getUrl("adminFeatureFlagsData"));
            await applyData(featureFlagData);
        })();

        return {
            activeUntil,
            flags,
            hasScope,
            flipEnabled,
            canEditFlag,
            getCurrentTenantStatus,
            getCurrentTenantOverride,
            getCurrentUserStatus,
            getCurrentUserOverride,
            tenants,
            users,
            toggleFlagOpen,
            isFlagOpen,
            splitIntoGroups,
            hasMassUpgradeableFlags,
            canEdit,
            massUpgradeableFlags,
            massUpgradeTarget,
            massUpgradeTenant,
            doMassUpgrade,
        };
    },
});
</script>
